import { useState, useEffect } from 'react';
import { Layout } from '@/components/ui/Layout';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/lib/supabase';
import { Calendar, Download, Loader2 } from 'lucide-react';
import {
  ResponseTrendsChart,
  DepartmentResponseChart,
  CompletionRatesChart,
  StatsOverview
} from '@/components/charts';

interface DashboardMetrics {
  total_surveys: number;
  active_surveys: number;
  total_responses: number;
  avg_completion_rate: number;
}

interface ResponseTrend {
  date: string;
  responses: number;
}

interface DepartmentMetrics {
  department: string;
  responses: number;
  invitations: number;
  completion_rate: number;
}

export function Reports() {
  const { organizationId } = useOrganization();
  const [timeRange, setTimeRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [responseTrends, setResponseTrends] = useState<ResponseTrend[]>([]);
  const [departmentMetrics, setDepartmentMetrics] = useState<DepartmentMetrics[]>([]);

  useEffect(() => {
    if (organizationId) {
      loadMetrics();
    }
  }, [organizationId, timeRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get departments with their metrics
      const { data: departmentData, error: departmentError } = await supabase
        .from('departments')
        .select(`
          id,
          name,
          organization_id
        `)
        .eq('organization_id', organizationId);

      if (departmentError) throw departmentError;

      // For each department, get response metrics
      const deptMetrics = await Promise.all((departmentData || []).map(async (dept) => {
        // Get respondents in this department
        const { data: respondents } = await supabase
          .from('survey_respondents')
          .select('id')
          .eq('department_id', dept.id);

        if (!respondents?.length) {
          return {
            department: dept.name,
            responses: 0,
            invitations: 0,
            completion_rate: 0
          };
        }

        const respondentIds = respondents.map(r => r.id);

        // Get all invitations for these respondents
        const { data: invitations } = await supabase
          .from('survey_invitations')
          .select('id, status')
          .in('respondent_id', respondentIds);

        // Get completed invitations
        const { data: completedInvitations } = await supabase
          .from('survey_invitations')
          .select('id')
          .in('respondent_id', respondentIds)
          .eq('status', 'completed');

        // Calculate metrics
        const totalInvitations = invitations?.length || 0;
        const completedCount = completedInvitations?.length || 0;

        // Calculate completion rate as percentage of completed invitations
        // Ensure it's capped at 100%
        const completion_rate = totalInvitations > 0 
          ? Math.min(Math.round((completedCount / totalInvitations) * 100), 100)
          : 0;

        return {
          department: dept.name,
          responses: completedCount,
          invitations: totalInvitations,
          completion_rate
        };
      }));

      // Sort by completion rate descending
      deptMetrics.sort((a, b) => b.completion_rate - a.completion_rate);
      setDepartmentMetrics(deptMetrics);

      // Get total and active surveys
      const { data: surveysData, error: surveysError } = await supabase
        .from('survey_templates')
        .select('id, status')
        .eq('organization_id', organizationId);

      if (surveysError) throw surveysError;

      const totalSurveys = surveysData?.length || 0;
      const activeSurveys = surveysData?.filter(s => s.status === 'published').length || 0;

      // Get total responses and completion rate
      const { data: responsesData, error: responsesError } = await supabase
        .from('survey_responses')
        .select(`
          id,
          template_id,
          respondent_id,
          submitted_at
        `)
        .eq('organization_id', organizationId);

      if (responsesError) throw responsesError;

      // Get total invitations
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('survey_invitations')
        .select('id, status')
        .in('survey_id', surveysData?.map(s => s.id) || []);

      if (invitationsError) throw invitationsError;

      const totalResponses = responsesData?.length || 0;
      const totalInvitations = invitationsData?.length || 0;
      
      // Ensure completion rate is capped at 100%
      const avgCompletionRate = totalInvitations > 0 
        ? Math.min(Math.round((totalResponses / totalInvitations) * 100), 100)
        : 0;

      // Calculate response trends
      const trends = responsesData?.reduce((acc: Record<string, number>, response) => {
        const date = new Date(response.submitted_at).toLocaleDateString();
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      const trendData = Object.entries(trends || {}).map(([date, responses]) => ({
        date,
        responses
      })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setMetrics({
        total_surveys: totalSurveys,
        active_surveys: activeSurveys,
        total_responses: totalResponses,
        avg_completion_rate: avgCompletionRate
      });
      setResponseTrends(trendData);

    } catch (err) {
      console.error('Error loading metrics:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Analytics & Reports</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Detailed insights and performance metrics
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
                <option value="year">Last 12 months</option>
              </select>
            </div>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <StatsOverview metrics={metrics} />

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ResponseTrendsChart data={responseTrends} />
          <DepartmentResponseChart data={departmentMetrics} />
          <CompletionRatesChart data={departmentMetrics} />
        </div>
      </div>
    </Layout>
  );
}